/// <amd-module name="VSSUI/Components/List/ListDropTargetBehavior" />
import * as React from "react";
import { getPointByEventType } from "VSSUI/Util";
import { IBehavior } from "VSSUI/Utilities/Behavior";
import { IEventDispatch } from "VSSUI/Utilities/Dispatch";
import { DragDropEffect } from "VSSUI/Utilities/DragDrop";
import { IItemProvider } from "VSSUI/Utilities/Provider";
import { cellFromEvent } from "./List";
import { BoltListDragEvent, IDragDroppableUI, IOverlayRenderProps } from "./List.Props";
import { ListDropIndicator } from "./ListDropIndicator";
import { ListDropIndicatorPosition } from "./ListDropIndicator.Props";

export interface IListDropData {
    /**
     * The index of the item in the list that is being dropped on.
     * The intention is the item will be dropped before this index. So, if the item will be at the
     * end of the list, the index will equal the length of the items.
     */
    index: number;
}

export interface IListDropTargetBehaviorOptions<D> {
    /**
     * A list of types of data which can be dropped on this list.
     */
    allowedTypes: string[];

    /**
     * A unique id for the drop target. Necessary for situations where
     * the drag source and drop target are separate lists.
     */
    id?: string;

    /**
     * Adjust some functionality if we are looking at a tree
     */
    isTree?: boolean;

    /**
     * Called with a dragenter event when the mouse has entered the list.
     */
    onDragEnter?: (event: BoltListDragEvent<HTMLElement, D>) => void;

    /**
     * Called with a dragexit event when the mouse has left the list.
     */
    onDragExit?: (event: BoltListDragEvent<HTMLElement, D>) => void;

    /**
     * Called with dragover events while the mouse is moved over the list.
     * If a certain row within the list should not be a viable drop target, then this callback
     * can set the dropEffect to none to indicate this.
     * If onDragOver returns a number, it will be used as the index for the drop indicator. Otherwise
     * it calculates index of indicator based on item being dragged on.
     */
    onDragOver?: (event: BoltListDragEvent<HTMLElement, D>, dropData: IListDropData) => number | void;

    /**
     * Called with a drop event when the drag operation has ended if the mouse is still over
     * the list and the dropEffect is not none.
     */
    onDrop?: (event: BoltListDragEvent<HTMLElement, D>, dropData: IListDropData) => void;

    /**
     * A string representing the type of data that is being acted upon in the
     * drag and drop operation.
     */
    type: string;
}

/**
 * A behavior that turns a list into the target of a drag and drop operation. Note - this
 * behavior should only be used if your list is intended to _only_ be a drop target. Please use
 * the ListDragDropBehavior instead if the list is also a drag source, as that is the only way
 * to get keyboard drag and drop support within your list.
 */
export class ListDropTargetBehavior<D> implements IBehavior<{}, IDragDroppableUI> {
    private dragDroppableUI: IDragDroppableUI;
    private eventDispatch: IEventDispatch;
    private indicatorName: string;
    private itemProvider: IItemProvider<D>;
    private listIndicatorPosition: ListDropIndicatorPosition;
    private options: IListDropTargetBehaviorOptions<D>;

    constructor(options: IListDropTargetBehaviorOptions<D>) {
        this.options = options;
    }

    public initialize = (props: any, dragDroppableUI: IDragDroppableUI, eventDispatch: IEventDispatch) => {
        this.dragDroppableUI = dragDroppableUI;
        this.eventDispatch = eventDispatch;

        this.eventDispatch.addEventListener("dragenter", this.onDragEnter);
        this.eventDispatch.addEventListener("dragexit", this.onDragExit);
        this.eventDispatch.addEventListener("dragover", this.onDragOver);
        this.eventDispatch.addEventListener("drop", this.onDrop);

        this.itemProvider = props.itemProvider;

        this.indicatorName = this.options.isTree ? "tree-drop-indicator" : "drop-indicator";
    };

    public componentDidUpdate(props: any) {
        this.itemProvider = props.itemProvider;
    }

    public componentWillUnmount() {
        this.eventDispatch?.removeEventListener("dragenter", this.onDragEnter);
        this.eventDispatch?.removeEventListener("dragexit", this.onDragExit);
        this.eventDispatch?.removeEventListener("dragover", this.onDragOver);
    }

    private calculateIndex(event: BoltListDragEvent<HTMLElement, D>): number {
        const cell = cellFromEvent(event);
        let index = cell.rowIndex;

        if (this.options.isTree) {
            return index;
        }

        if (cell.rowElement && event.detail.dataTransfer.secondaryData) {
            const dragIndex = event.detail.dataTransfer.secondaryData.index;
            const listId = event.detail.dataTransfer.secondaryData.sourceId;
            const nativeEvent = event.detail.nativeEvent;
            const rowRect = cell.rowElement!.getBoundingClientRect();

            const point = getPointByEventType(nativeEvent);
            const topHalfOfRow = point ? point.y < rowRect.height / 2 + rowRect.top : index < dragIndex;

            if (this.options.id !== listId) {
                this.listIndicatorPosition = ListDropIndicatorPosition.top;
                if (!topHalfOfRow) {
                    index++;
                }
                if (index >= this.itemProvider.length) {
                    this.listIndicatorPosition = ListDropIndicatorPosition.bottom;
                    index--;
                }
            } else {
                if (index < dragIndex) {
                    this.listIndicatorPosition = ListDropIndicatorPosition.top;
                    if (!topHalfOfRow) {
                        index++;
                    }
                } else if (index > dragIndex) {
                    this.listIndicatorPosition = ListDropIndicatorPosition.bottom;
                    if (topHalfOfRow) {
                        index--;
                    }
                }
                // No-op if index === dragIndex
            }
        }
        return index;
    }

    private handlesType(event: BoltListDragEvent<HTMLElement, D>): boolean {
        const type = event.detail.dataTransfer?.type || "";
        return this.options.allowedTypes.indexOf(type) !== -1;
    }

    private onDragEnter = (event: BoltListDragEvent<HTMLElement, D>) => {
        if (!this.handlesType(event)) {
            return;
        }

        if (this.options.onDragEnter) {
            this.options.onDragEnter(event);
        } else {
            event.detail.dataTransfer.dropEffect = DragDropEffect.move;
        }
    };

    private onDragExit = (event: BoltListDragEvent<HTMLElement, D>) => {
        if (!this.handlesType(event)) {
            return;
        }

        if (this.options.onDragExit) {
            this.options.onDragExit(event);
        }
        this.dragDroppableUI.removeOverlay(this.indicatorName);
    };

    private onDragOver = (event: BoltListDragEvent<HTMLElement, D>) => {
        if (!this.handlesType(event)) {
            return;
        }

        let index = this.calculateIndex(event);
        const dragIndex = event.detail.dataTransfer.secondaryData!.index;
        const listId = event.detail.dataTransfer.secondaryData!.sourceId;

        let resultFromDragOver;
        if (index >= 0 && (index !== dragIndex || listId !== this.options.id || this.options.isTree)) {
            if (this.options.onDragOver) {
                resultFromDragOver = this.options.onDragOver(event, {
                    index: this.listIndicatorPosition === ListDropIndicatorPosition.bottom ? index + 1 : index
                });
                if (typeof(resultFromDragOver) === "number") {
                    index = resultFromDragOver;
                }
            } else {
                event.detail.dataTransfer.dropEffect = DragDropEffect.move;
            }
        } else {
            event.detail.dataTransfer.dropEffect = DragDropEffect.none;
        }

        if (event.detail.dataTransfer.dropEffect === DragDropEffect.none) {
            this.dragDroppableUI.removeOverlay(this.indicatorName);
        } else {
            if (typeof(resultFromDragOver) === "number") {
                this.listIndicatorPosition = ListDropIndicatorPosition.bottom;
            }
            this.dragDroppableUI.addOverlay(this.indicatorName, index, this.renderDropIndicator);
        }
    };

    private onDrop = (event: BoltListDragEvent<HTMLElement, D>) => {
        if (!this.handlesType(event)) {
            return;
        }

        const index = this.calculateIndex(event);
        const dragIndex = event.detail.dataTransfer.secondaryData!.index;
        const listId = event.detail.dataTransfer.secondaryData!.sourceId;

        if (index >= 0 && (index !== dragIndex || listId !== this.options.id) && this.options.onDrop) {
            this.options.onDrop(event, { index: this.listIndicatorPosition === ListDropIndicatorPosition.bottom ? index + 1 : index });
        }
        this.dragDroppableUI.removeOverlay(this.indicatorName);
    };

    private renderDropIndicator = (props: IOverlayRenderProps) => {
        return this.options.isTree ? (
            <div className="bolt-list-tree-drop-target flex-grow" />
        ) : (
            <ListDropIndicator position={this.listIndicatorPosition} />
        );
    };
}
